import { Flow, SelectHelperMenuItem, Text, useLocale } from 'bold-ui'
import { CustomSelectFieldProps, SelectField } from 'components/form'
import {
  getLoteImunobiologicoFabricanteExpandedLabel,
  getLoteImunobiologicoFabricanteLabel,
} from 'components/form/field/select/LoteImunobiologicoSelectField/LoteImunobiologicoSelectField'
import { DEFAULT_SELECT_PAGE_PARAM, useAsyncQuerySelect } from 'components/form/field/select/useAsyncQuerySelect'
import { css } from 'emotion'
import { LoteImunobiologicoFabricanteDocument } from 'graphql/hooks.generated'
import { LoteImunobiologicoFabricanteQuery, LoteImunobiologicoFabricanteQueryVariables } from 'graphql/types.generated'

export type LoteImunobiologicoFabricanteSelectFieldModel = Partial<
  LoteImunobiologicoFabricanteQuery['fabricantesImunobiologico']['content'][0]
>

export type LoteImunobiologicoFabricanteSelectFieldProps = CustomSelectFieldProps<
  LoteImunobiologicoFabricanteSelectFieldModel
>

export function LoteImunobiologicoFabricanteSelectField(props: Readonly<LoteImunobiologicoFabricanteSelectFieldProps>) {
  const { selectProps: asyncProps, skipping } = useAsyncQuerySelect<
    LoteImunobiologicoFabricanteSelectFieldModel,
    LoteImunobiologicoFabricanteQuery,
    LoteImunobiologicoFabricanteQueryVariables
  >({
    query: LoteImunobiologicoFabricanteDocument,
    extractItems: (data) => data?.fabricantesImunobiologico?.content,
    variables: (inputQuery: string) => ({
      input: {
        query: inputQuery,
        pageParams: {
          page: {
            ...DEFAULT_SELECT_PAGE_PARAM,
            size: 5,
            sort: ['codigoRnds'],
          },
        },
        showOnlyAtivos: true,
      },
    }),
    fetchPolicy: 'cache-first',
    skip: (inputString) => inputString?.trim()?.length < 2,
  })

  const itemToString = (item: LoteImunobiologicoFabricanteSelectFieldModel) =>
    getLoteImunobiologicoFabricanteItemText(item)

  return (
    <SelectField<LoteImunobiologicoFabricanteSelectFieldModel>
      itemToString={itemToString}
      renderItem={renderItem}
      components={{ EmptyItem: () => EmptyMenuItem({ skipping: !!skipping }) }}
      {...asyncProps}
      {...props}
    />
  )
}

export const getLoteImunobiologicoFabricanteItemText = (item: LoteImunobiologicoFabricanteSelectFieldModel) => {
  if (!item) return ''

  return getLoteImunobiologicoFabricanteLabel(item)
}

export const getLoteImunobiologicoFabricanteItemSubtitle = (item: LoteImunobiologicoFabricanteSelectFieldModel) => {
  if (!item?.codigoRnds) return undefined

  return getLoteImunobiologicoFabricanteExpandedLabel(item) !== getLoteImunobiologicoFabricanteLabel(item)
    ? item.nome
    : undefined
}

const renderItem = (item: LoteImunobiologicoFabricanteSelectFieldModel) => {
  return (
    item && (
      <Flow direction='vertical' gap={0}>
        <Flow direction='horizontal' gap={0.5}>
          <Text
            style={css`
              word-break: break-all;
            `}
          >
            <strong>{getLoteImunobiologicoFabricanteItemText(item)}</strong>
          </Text>
        </Flow>
        {getLoteImunobiologicoFabricanteItemSubtitle(item) && (
          <Flow direction='horizontal' gap={0.5}>
            <Text>{getLoteImunobiologicoFabricanteItemSubtitle(item)}</Text>
          </Flow>
        )}
      </Flow>
    )
  )
}

const EmptyMenuItem = ({ skipping }: { skipping: boolean }) => {
  const locale = useLocale()

  return (
    <SelectHelperMenuItem>
      {skipping ? 'Digite para buscar um fabricante.' : locale.select.emptyItem}
    </SelectHelperMenuItem>
  )
}
